import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

/**
 * ORDER-CONTENT-01 — THE accepted offer's patient-visible scope.
 *
 * Lifted verbatim out of the MyOffers detail modal (itemised breakdown,
 * included items, additional services) so the order screen shows the SAME
 * rendering of the SAME backend fields. It is deliberately not a second
 * interpretation of an offer: every amount here is a line item the provider
 * authored, and the contractual total stays with `financial_summary`
 * (rendered by the caller), never a locally summed subset.
 *
 * Presentation only — quantity × unit price per row is the provider's own
 * line arithmetic, exactly as the offer and the PDF already display it.
 */
export const OfferScopeDetails = ({ offer, idBase }) => {
  const { t } = useTranslation();
  if (!offer) return null;
  const base = idBase || offer.quote_id || offer.id || 'offer';
  const currency = offer.currency || '';

  return (
    <>
      {offer.offer_items?.length > 0 && (
        <div data-testid="offer-items-breakdown">
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            {t('patient.offers.itemizedBreakdown', 'Itemized Breakdown')}
          </h3>
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colItem', 'Item')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colDescription', 'Description')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colQty', 'Qty')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colUnitPrice', 'Unit Price')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colLineTotal', 'Line Total')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offer.offer_items.map((item, idx) => {
                  const qty = Number(item.quantity) || 1;
                  const unit = Number(item.price) || 0;
                  const line = unit * qty;
                  const cur = item.currency || currency;
                  return (
                    <tr key={idx} data-testid={`patient-offer-item-row-${base}-${idx}`}>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium capitalize">
                          {(item.item_type || item.name || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{item.description || '-'}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{qty}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{cur} {unit.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-medium">{cur} {line.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {offer.included_items?.length > 0 && (
        <div data-testid="offer-included-items">
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            {t('patient.offers.includedInPackage', 'Included in Package')}
          </h3>
          <div className="space-y-1">
            {offer.included_items
              .filter((item) => (typeof item === 'string' ? true : item.included !== false))
              .map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm" data-testid={`offer-included-item-${base}-${idx}`}>
                  <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span>{typeof item === 'string' ? item : item.name}</span>
                    {typeof item !== 'string' && item.description && (
                      <p className="text-xs text-gray-500">{item.description}</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {offer.additional_services?.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            {t('patient.offers.additionalServices', 'Additional Services')}
          </h3>
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="patient-additional-services-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colService', 'Service')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colQty', 'Qty')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colUnitPrice', 'Unit Price')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.colLineTotal', 'Line Total')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offer.additional_services.map((s, idx) => {
                  const qty = Number(s.quantity) || 1;
                  const unit = Number(s.price) || 0;
                  const line = unit * qty;
                  return (
                    <tr key={idx} data-testid={`patient-add-svc-row-${base}-${idx}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{s.name}</div>
                        {s.description && <div className="text-xs text-gray-500">{s.description}</div>}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{qty}</td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {unit > 0 ? `${currency} ${unit.toLocaleString()}`
                          : <span className="text-green-700">{t('patient.offers.includedPrice', 'Included')}</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {line > 0 ? `${currency} ${line.toLocaleString()}`
                          : <span className="text-green-700">{t('patient.offers.includedPrice', 'Included')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default OfferScopeDetails;
